import cx from 'classnames'
import { FormattedMessage, useIntl } from 'locales/index'
import React from 'react'
import { connect, useSelector } from 'react-redux'
import { AppSpacing, BrandedLogo, Col, Divider, MarkdownWrapper, Row, Typography } from '@commutifi-fe/ui'
import { CommutifiSvg, CommutifiSvgProps } from '@commutifi-fe/custom-icons/svgs'
import { selectors, selectors as surveySelectors } from 'store/modules/enterpriseSurvey'
import SurveyLayout from 'views/Anonymous/views/Onboarding/layout/SurveyLayout'
import { colors } from 'constants/global'

import { ExpiredSurvey } from './components/ExpiredSurvey'
import { StartSurveyButtons } from './components/StartSurveyButtons'
import { SurveyLandingPageCardLayout } from './layout/SurveyLandingPageCardLayout'
import { SurveyLandingPageLayout } from './layout/SurveyLandingPageLayout'
import styles from './styles.module.scss'

const { Text } = Typography

interface SurveyLandingPageProps {
  backgroundImage?: string
  introHeader?: string
  introContent?: string
  introName?: string
  showOverview?: boolean
  username?: string
  address?: string
  logo?: string
  isExpired?: boolean
  onStartClick: React.MouseEventHandler
  /**
   * If this prop is defined we will add a button 'I already have an account'
   * that will call this callback function onClick
   */
  redirectToLogin: React.MouseEventHandler | undefined
  header?: React.ReactNode
}

export const registerScreenPathRegexp = /^(\/to\/)[a-zA-Z0-9-]+(\/onboarding\/register)$/

function ModeIconCircle({
  image,
  alt,
  fill,
  backgroundColor
}: {
  image: CommutifiSvgProps['image']
  alt: string
  fill?: string
  backgroundColor?: string
}) {
  return (
    <div className={styles['mode-icon-circle']}>
      <div
        style={{ borderRadius: '50%', backgroundColor: backgroundColor || colors.neutral200 }}
        className="u-padding--medium"
      >
        <CommutifiSvg image={image} width={50} height={50} alt={alt} fill={fill} />
      </div>
    </div>
  )
}

function ModeIcons() {
  const intl = useIntl()
  return (
    <div>
      <Divider />
      <Text strong="semi-bold" block className="u-margin-bottom--medium-2">
        {intl.formatMessage({ id: 'intro.commuteInfo.title' })}
      </Text>
      <AppSpacing className="u-flex-align-center u-flex-space-between u-flex-wrap">
        <ModeIconCircle image={CommutifiSvg.Images.Walk} alt="Walk" backgroundColor={colors.primary100} />
        <ModeIconCircle image={CommutifiSvg.Images.Shuttle} alt="Shuttle" backgroundColor={colors.primary100} />
        <ModeIconCircle
          image={CommutifiSvg.Images.Bike}
          alt="Bike"
          fill={colors.accentChartreuse500}
          backgroundColor={colors.accentChartreuse200}
        />
        <ModeIconCircle
          image={CommutifiSvg.Images.PublicTransit}
          alt="Public Transit"
          fill={colors.accentChartreuse500}
          backgroundColor={colors.accentChartreuse200}
        />
        <ModeIconCircle image={CommutifiSvg.Images.Car} alt="Car" backgroundColor={colors.accentOrange100} />
        <ModeIconCircle image={CommutifiSvg.Images.Carpool} alt="Carpool" backgroundColor={colors.accentPurple100} />
        <ModeIconCircle image={CommutifiSvg.Images.Vanpool} alt="Vanpool" backgroundColor={colors.accentPurple100} />
      </AppSpacing>
    </div>
  )
}

export function SurveyLandingPage({
  backgroundImage,
  introHeader,
  introContent,
  introName,
  showOverview,
  username,
  address,
  logo,
  header,
  isExpired,
  onStartClick,
  redirectToLogin
}: SurveyLandingPageProps) {
  const intl = useIntl()
  const customBranding = useSelector(selectors.getSurveyCustomBranding)

  // See if we have a username, if not we fallback to the survey introContent,
  // if not we fallback to the ent/org name
  const title = username ? <FormattedMessage id="intro.hello" values={{ name: username }} /> : introHeader || introName

  const PageTitle = title ? (
    <div className={cx(styles.title, 'u-font-24', backgroundImage ? 'u-color--white' : 'u-color--neutral-600')}>
      {title}
    </div>
  ) : null

  const Logo = logo ? (
    <div>
      <img src={logo} alt="Company logo" className="u-margin-bottom--small" />
    </div>
  ) : null

  return (
    <SurveyLayout>
      <SurveyLandingPageLayout backgroundImgSrc={backgroundImage}>
        {header ? (
          <div className={cx(!backgroundImage ? 'u-color--neutral-600' : 'u-color--white')} style={{ width: '100%' }}>
            {header}
          </div>
        ) : null}
        <BrandedLogo logoURLs={customBranding.logoUrl} white={Boolean(backgroundImage)} />
        <span className={styles['survey-title']}>{PageTitle}</span>
        {address ? <span className={backgroundImage ? 'u-color--white' : 'u-color--primary'}>{address}</span> : null}
        <SurveyLandingPageCardLayout>
          {isExpired ? (
            <ExpiredSurvey />
          ) : (
            <Row gutter={40}>
              {/* LEFT COLUMN (Dynamic content) */}
              <Col span={24} className="u-margin-bottom--medium">
                {Logo}
                <MarkdownWrapper source={introContent || intl.formatMessage({ id: 'intro.defaultContent' })} />
                {showOverview && <ModeIcons />}
              </Col>
            </Row>
          )}
        </SurveyLandingPageCardLayout>
        <StartSurveyButtons startSurvey={isExpired ? undefined : onStartClick} redirectToLogin={redirectToLogin} />
      </SurveyLandingPageLayout>
    </SurveyLayout>
  )
}

const mapStateToProps = (state: any) => ({
  backgroundImage: surveySelectors.makeGetSurvey('backgroundImage')(state),
  introHeader: surveySelectors.makeGetContent('introHeader')(state),
  introContent: surveySelectors.makeGetContent('introContent')(state),
  introName:
    surveySelectors.makeGetPrefilledData('enterprise.name')(state) ||
    surveySelectors.makeGetPrefilledData('organization.name')(state),
  showOverview: surveySelectors.makeGetSurvey('showOverview')(state),
  isExpired: surveySelectors.makeGetPrefilledData('isExpired')(state),
  username: surveySelectors.makeGetPrefilledData('account.name')(state),
  address: surveySelectors.makeGetPrefilledData('building.address')(state),
  logo: surveySelectors.getSurveyLogo(state)
})

export const SurveyLandingPageContainer = connect(mapStateToProps, null)(SurveyLandingPage)
